import { schema } from 'normalizr';
import { CALL_API } from '../middleware/api';

// ACTIONS
export const END_USER_FETCH_REQUEST = 'endUsers/END_USER_FETCH_REQUEST';
export const END_USER_FETCH_SUCCESS = 'endUsers/END_USER_FETCHED_SUCCESS';
export const END_USER_FETCH_ERROR = 'endUsers/END_USER_FETCHED_ERROR';

export const END_USERS_FETCH_REQUEST = 'endUsers/END_USERS_FETCH_REQUEST';
export const END_USERS_FETCH_SUCCESS = 'endUsers/END_USERS_FETCHED_SUCCESS';
export const END_USERS_FETCH_ERROR = 'endUsers/END_USERS_FETCHED_ERROR';

export const END_USER_CREATE_REQUEST = 'endUsers/END_USER_CREATE_REQUEST';
export const END_USER_CREATE_SUCCESS = 'endUsers/END_USER_CREATE_SUCCESS';
export const END_USER_CREATE_ERROR = 'endUsers/END_USER_CREATE_ERROR';

export const END_USER_MODIFY_REQUEST = 'endUsers/END_USER_MODIFY_REQUEST';
export const END_USER_MODIFY_SUCCESS = 'endUsers/END_USER_MODIFY_SUCCESS';
export const END_USER_MODIFY_ERROR = 'endUsers/END_USER_MODIFY_ERROR';

export const END_USER_UNLOAD_CURRENT = 'endUsers/END_USER_UNLOAD_CURRENT';

export const CHANGE_USER_VALID_STATUS_REQUEST = 'endUsers/CHANGE_USER_VALID_STATUS_REQUEST';
export const CHANGE_USER_VALID_STATUS_SUCCESS = 'endUsers/CHANGE_USER_VALID_STATUS_SUCCESS';
export const CHANGE_USER_VALID_STATUS_ERROR = 'endUsers/CHANGE_USER_VALID_STATUS_ERROR';

export const CHANGE_USER_ACTIVATION_STATUS_REQUEST =
  'endUsers/CHANGE_USER_ACTIVATION_STATUS_REQUEST';
export const CHANGE_USER_ACTIVATION_STATUS_SUCCESS =
  'endUsers/CHANGE_USER_ACTIVATION_STATUS_SUCCESS';
export const CHANGE_USER_ACTIVATION_STATUS_ERROR = 'endUsers/CHANGE_USER_ACTIVATION_STATUS_ERROR';

export const DEVICE_BY_USER_FETCH_REQUEST = 'endUsers/DEVICE_BY_USER_FETCH_REQUEST';
export const DEVICE_BY_USER_FETCH_SUCCESS = 'endUsers/DEVICE_BY_USER_FETCH_SUCCESS';
export const DEVICE_BY_USER_FETCH_ERROR = 'endUsers/DEVICE_BY_USER_FETCH_ERROR';

// REDUCER
export default function reducer(
  state = {
    entities: null,
    result: [],
    current: null,
    fetching: false,
    fetched: false,
    error: null,
    devices: null,
  },
  action
) {
  switch (action.type) {
    case END_USERS_FETCH_REQUEST:
    case END_USER_FETCH_REQUEST:
      return {
        ...state,
        fetching: true,
      };
    case END_USERS_FETCH_SUCCESS:
      return {
        ...state,
        fetching: false,
        fetched: true,
        entities: action.payload.entities.endUsers,
        result: action.payload.result,
      };

    case DEVICE_BY_USER_FETCH_SUCCESS: {
      return {
        ...state,
        devices: action.payload,
      }
    }

    case END_USER_FETCH_SUCCESS:
      return {
        ...state,
        fetching: false,
        fetched: true,
        current: action.payload,
      };
    case END_USERS_FETCH_ERROR:
    case END_USER_FETCH_ERROR:
      return {
        ...state,
        fetching: false,
        error: action.payload,
      };

    case END_USER_UNLOAD_CURRENT:
      return {
        ...state,
        current: null,
      };

    case CHANGE_USER_VALID_STATUS_SUCCESS: {
      const userId = action.id;
      const newValidStatus = action.newValidStatus;
      const list = Object.assign({}, state.entities);

      if (userId in list) {
        list[userId].valid = newValidStatus;
      }

      const newCurrent = Object.assign({}, state.current);
      if (state.current && state.current.id === userId) {
        newCurrent.valid = newValidStatus;
      }

      return {
        ...state,
        current: newCurrent,
        entities: list,
      };
    }

    case CHANGE_USER_ACTIVATION_STATUS_SUCCESS: {
      const userId = action.id;
      const newActivationStatus = action.newActivationStatus;
      const list = Object.assign({}, state.entities);

      if (userId in list) {
        list[userId].is_active = newActivationStatus;
      }

      const newCurrent = Object.assign({}, state.current);
      if (state.current && state.current.id === userId) {
        newCurrent.is_active = newActivationStatus;
      }

      return {
        ...state,
        current: newCurrent,
        entities: list,
      };
    }
  }

  return state;
}

// Schemas
const endUserSchema = new schema.Entity('endUsers');

// Operations
export function fetchEndUsers(params) {
  return {
    [CALL_API]: {
      types: [END_USERS_FETCH_REQUEST, END_USERS_FETCH_SUCCESS, END_USERS_FETCH_ERROR],
      promise: httpClient => httpClient.post('/eu/enduser', params),
      schema: { data: [endUserSchema] },
    },
  };
}

export function fetchEndUser(id) {
  return function (dispatch, getState) {
    const endUsersState = getState().endUsers;

    if (endUsersState.entities && id in endUsersState.entities) {
      // endUser already exists in store
      dispatch({ type: END_USER_FETCH_SUCCESS, payload: endUsersState.entities[id] });
    } else {
      dispatch({
        [CALL_API]: {
          types: [END_USER_FETCH_REQUEST, END_USER_FETCH_SUCCESS, END_USER_FETCH_ERROR],
          promise: httpClient => httpClient.get(`/eu/enduser/${id}`),
        },
      });
    }
  };
}

export function unloadCurrent() {
  return function (dispatch) {
    dispatch({ type: END_USER_UNLOAD_CURRENT });
  };
}

export function createEndUser(endUser) {
  return {
    [CALL_API]: {
      types: [END_USER_CREATE_REQUEST, END_USER_CREATE_SUCCESS, END_USER_CREATE_ERROR],
      promise: httpClient => httpClient.post('/eu/enduser/modify', endUser),
      successMessage: 'User successfully created',
    },
  };
}

export function modifyEndUser(endUser) {
  return {
    [CALL_API]: {
      types: [END_USER_MODIFY_REQUEST, END_USER_MODIFY_SUCCESS, END_USER_MODIFY_ERROR],
      promise: httpClient => httpClient.post('/eu/enduser/modify', endUser),
      successMessage: 'User successfully modified',
    },
  };
}

export function changeValidStatus(userId, enable = true) {
  const action = enable ? 'validate' : 'invalidate';
  const textVerb = enable ? 'recovered' : 'deleted';
  return {
    id: userId,
    newValidStatus: enable,
    [CALL_API]: {
      types: [
        CHANGE_USER_VALID_STATUS_REQUEST,
        CHANGE_USER_VALID_STATUS_SUCCESS,
        CHANGE_USER_VALID_STATUS_ERROR,
      ],
      promise: httpClient => httpClient.get(`/eu/${action}/${userId}`),
      successMessage: `User successfully ${textVerb}`,
    },
  };
}

export function changeActivationStatus(userId, activate = true) {
  if (!activate) return; // Currently only activation is possible

  return {
    id: userId,
    newActivationStatus: activate,
    [CALL_API]: {
      types: [
        CHANGE_USER_ACTIVATION_STATUS_REQUEST,
        CHANGE_USER_ACTIVATION_STATUS_SUCCESS,
        CHANGE_USER_ACTIVATION_STATUS_ERROR,
      ],
      promise: httpClient => httpClient.get(`/eu/activate/${userId}`),
      successMessage: 'User successfully activated',
    },
  };
}

export function fetchDevicesByEndUser(id) {
  return {
    [CALL_API]: {
      types: [DEVICE_BY_USER_FETCH_REQUEST, DEVICE_BY_USER_FETCH_SUCCESS, DEVICE_BY_USER_FETCH_ERROR],
      promise: httpClient => httpClient.get(`/eu/deviceList/${id}`),
    },
  };
}



// WEBPACK FOOTER //
// ./src/redux/modules/endUsers.js